import { useCallback, useState } from 'react';
import { baseUrl } from '../../../redux/api/fymAPI';
import { trackEvent } from '../../analytics/sml/analytics';
import { useUserToken } from '../../../redux/slices/appInfo/hooks';
import { downloadFile } from '../../../utils/qrCode/qrCodeSvg';
import { useMusic24Capabilities } from '../hooks/useMusic24Capabilities';
import { useUpgradePrompt } from '../hooks/useUpgradePrompt';
import { SMLUpgradeFeature } from '../config/music24PlanCapabilities';

const FALLBACK_FILENAME = 'subscribers.csv';

function filenameFromResponse(response: Response): string {
  const disposition = response.headers.get('Content-Disposition');
  const match = disposition === null ? null : /filename="([^"]+)"/.exec(disposition);
  return match?.[1] ?? FALLBACK_FILENAME;
}

/**
 * Downloads the subscribers of the account as a CSV (`GET /accounts/:accountId/fans/subscribers.csv`).
 * It is the approach of `useExportPresaves`: the shared base query parses JSON, so this is a plain
 * fetch with the bearer token, and the body becomes a file. With no `canExportFans` (Free) the
 * button opens the plan prompt (design c6).
 */
export function useExportFans(accountId: number) {
  const userToken = useUserToken();
  const [isExporting, setIsExporting] = useState(false);
  const isLocked = !useMusic24Capabilities().canExportFans;
  const openUpgradePrompt = useUpgradePrompt();

  const exportFans = useCallback(() => {
    if (isLocked) {
      openUpgradePrompt(SMLUpgradeFeature.fanExport);
      return;
    }
    if (userToken === null || userToken === undefined) {
      return;
    }
    setIsExporting(true);
    window
      .fetch(`${baseUrl}accounts/${accountId}/fans/subscribers.csv`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${userToken}` },
      })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Could not export the subscribers: ${response.status}`);
        }
        const blob = await response.blob();
        downloadFile(filenameFromResponse(response), URL.createObjectURL(blob), true);
        trackEvent('fans_exported', { kind: 'fans' });
      })
      .catch(console.error)
      .finally(() => setIsExporting(false));
  }, [isLocked, openUpgradePrompt, accountId, userToken]);

  return { exportFans, isExporting, isLocked };
}
